// @ts-nocheck
import { ANSWER_SELECTOR_TYPES } from '@scenes/HomeHub/homehub.constants';
import { isValidFilteredEmptyArray, isValidNonEmptyArray } from '@utils/array';
import { initialDropdownPanelState } from '@scenes/HomeHub/components/Selectors/Device/DeviceDropdownSelectorGroup';

/**
 * Data normalization for Element Selectors Both for:
 *   1. Pushing it into selected_answers
 *   2. Pushing it into params for the API update call.
 */
import { v4 as uuid } from 'uuid';

export type TAnswerInputAddress = {
  data_type: string;
  data_value: string;
  data_qty: null;
  home_survey_answer_id: null;
  entity: null;
};

const getStaticParams = args => ({ ...args });
export const answerSchemaNormalizer = ({
  currentQuestionId,
  currentSelectedAnswers,
  questionSeoName,
  hid,
  /* Just a quality of life helper */
  staticParams = getStaticParams({ seoName: questionSeoName, hid }),
}) => {
  const answerSchema = new Map([
    /* ---------------------------------------------------- */
    /* ----------------- CHECKBOX ------------------------- */
    /* ---------------------------------------------------- */
    [
      ANSWER_SELECTOR_TYPES.checkbox,
      () => ({
        selectedAnswers: ({ selectedAnswers }) => ({
          read() {
            /* no normalization needs to happen */
            return selectedAnswers;
          },
          write(submittedAnswers) {
            /* Note: there is no checkbox with "Other" So, if that gets added, that needs to be tested & fleshed out */
            return submittedAnswers.map(id => ({
              home_survey_answer_id: id,
            }));
          },
        }),
        params: submittedAnswer => ({
          id: currentQuestionId,
          answers: submittedAnswer.map(d => ({ id: d.home_survey_answer_id })),
          ...staticParams,
        }),
      }),
    ],

    /* ---------------------------------------------------- */
    /* ----------------- RADIO  --------------------------- */
    /* ---------------------------------------------------- */
    [
      ANSWER_SELECTOR_TYPES.radio,
      () => ({
        selectedAnswers: ({ selectedAnswers }) => ({
          read() {
            const [answer] = selectedAnswers;

            if (!answer) return [];

            return {
              ...answer,
              id: answer.home_survey_answer_id,
              ...(answer.custom && { custom: answer.custom }),
            };
          },
          write(submittedAnswer) {
            return {
              ...submittedAnswer,
              home_survey_answer_id: submittedAnswer.id,
              data_value: submittedAnswer.custom ?? '',
            };
          },
        }),
        params: submittedAnswer => ({
          id: currentQuestionId,
          answers: submittedAnswer.map(d => ({ id: d.home_survey_answer_id, ...(d.data_value ? { data_value: d.data_value } : {}) })),
          ...staticParams,
        }),
      }),
    ],

    /* ---------------------------------------------------- */
    /* ----------------- INPUT - ADDRESS ------------------ */
    /* ---------------------------------------------------- */
    [
      ANSWER_SELECTOR_TYPES.input_address,
      () => ({
        selectedAnswers: ({ selectedAnswers }) => ({
          /* No transforms */
          read() {
            if (!selectedAnswers || !isValidNonEmptyArray(selectedAnswers)) return [];

            return selectedAnswers;
          },
          write(submittedAnswer) {
            if (!submittedAnswer?.trim()) return [];

            if (Array.isArray(submittedAnswer)) {
              const [sa = {}] = submittedAnswer;
              return [
                {
                  ...sa,
                  data_value: sa.data_value,
                },
              ];
            }

            return [
              {
                data_value: submittedAnswer,
              },
            ];
          },
        }),
        params: submittedAnswer => ({
          id: currentQuestionId,
          answers: submittedAnswer.map(({ data_value }) => ({ data_value })),
          ...staticParams,
        }),
      }),
    ],

    /* ---------------------------------------------------- */
    /* ----------------- DEVICES -------------------------- */
    /* ---------------------------------------------------- */
    [
      ANSWER_SELECTOR_TYPES.device,
      () => ({
        selectedAnswers: ({ devices, selectedAnswers } = {}) => ({
          read() {
            /* Nothing is saved, send up the default */
            if (!selectedAnswers || selectedAnswers.length === 0) return initialDropdownPanelState;
            /* If the answer is "I don't have any": only dealing w/ one for now */
            const [dontHaveAny] = selectedAnswers;
            const selectedDevices = selectedAnswers.map(answer => {
              /* We have a secondary answer concern, should format */
              if (!answer.entity) {
                return {
                  id: answer.home_survey_answer_id || answer.id,
                };
              }

              /* Do we have both "other" with a dropdown selection? */
              const determineMixedResponse = devices.some(device => device.make_id === answer.entity?.make_id);
              const findMatchingDeviceByMake = devices.find(device => device.make_id === answer.entity?.make_id);

              return {
                ...answer,
                id: dontHaveAny.id || uuid(),
                ...(answer.entity.custom && !answer.entity.idk
                  ? {
                      showInput: !determineMixedResponse,
                      showModelInput: !this.showInput,
                    }
                  : {
                      showInput: false,
                      showModelInput: false,
                    }),
                entity: {
                  ...answer.entity,
                  qty: answer.data_qty,
                  ...(answer.entity.idk && {
                    make_id: findMatchingDeviceByMake ? findMatchingDeviceByMake.make_id : -1,
                    model_id: -1,
                  }),
                },
              };
            });

            return selectedDevices;
          },
          write() {},
        }),
        params: data => {
          const sanitizedAnswers = data.map(answer => {
            if (!answer.entity) {
              return {
                id: answer.id,
              };
            }

            const { entity, data_qty: dataQty, qty } = answer;
            return {
              data_qty: dataQty || qty || entity.qty,
              make: entity.make_id === 'other' ? entity.make_name : entity.make_id,
              model: entity.model_id === 'other' ? entity.model_name : entity.model_id,
            };
          });

          return {
            id: currentQuestionId,
            answers: sanitizedAnswers,
            ...staticParams,
          };
        },
      }),
    ],

    /* ------------------------------------------------------ */
    /* ----------------- DROPDOWN  -------------------------- */
    /* ------------------------------------------------------ */
    [
      ANSWER_SELECTOR_TYPES.dropdown,
      () => ({
        selectedAnswers: ({ answers, selectedAnswers }) => ({
          read() {
            if (!selectedAnswers || !isValidNonEmptyArray(selectedAnswers)) return [];

            const [userAnswer] = selectedAnswers;
            const matchedAnswer = answers.find(a => a.id === userAnswer.home_survey_answer_id);

            return [
              {
                ...matchedAnswer,
                label: matchedAnswer.text,
                value: matchedAnswer.id,
                ...(matchedAnswer.custom_input && { label: userAnswer.data_value }),
              },
            ];
          },
          write(submittedAnswers) {
            if (!submittedAnswers || !isValidFilteredEmptyArray(submittedAnswers)) return [];

            return submittedAnswers.map(answer => ({
              ...(answer.custom_input && { label: answer.text, id: answer.id, custom_input: true }),
              ...(!answer.custom_input && { text: answer.label, id: answer.value, custom_input: false }),
            }));
          },
        }),
        params: submittedAnswer => ({
          id: currentQuestionId,
          answers: submittedAnswer.map(d => ({ id: d.id, ...(d.custom_input && { data_value: d.label }) })),
          ...staticParams,
        }),
      }),
    ],
  ]);
  /* We have redundancy here, so lets just copy so we keep same strategy for all input question types */
  answerSchema
    .set(ANSWER_SELECTOR_TYPES.input_numeric, answerSchema.get(ANSWER_SELECTOR_TYPES.input_address))
    .set(ANSWER_SELECTOR_TYPES.input_currency, answerSchema.get(ANSWER_SELECTOR_TYPES.input_address))
    .set(ANSWER_SELECTOR_TYPES.input, answerSchema.get(ANSWER_SELECTOR_TYPES.input_address))
    .set(ANSWER_SELECTOR_TYPES.input_text, answerSchema.get(ANSWER_SELECTOR_TYPES.input_address));

  return answerSchema;
};

export default answerSchemaNormalizer;
